/**
 * Ouverture d'une notification push touchée (décision 1 du 19/10/2026) : l'avis
 * est marqué lu et la page de l'élément concerné s'ouvre à sa place, avec les
 * mêmes règles que la cloche (`notificationTarget`). Sans destination connue :
 * la boîte de réception. Les données arrivent après « # » (`public/sw-push.js`) :
 * elles ne partent jamais au serveur.
 *
 * Avis d'une autre entreprise du compte (25/10/2026) : l'application y passe
 * d'abord, puis ouvre l'élément. Bascule impossible (hors ligne, accès fermé) :
 * le message s'affiche et rien ne s'ouvre dans la mauvaise entreprise.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/api.js';
import { useAuth } from '../../core/auth.js';
import { t } from '../../core/i18n.js';
import { membershipsOf } from '../../core/memberships.js';
import { notificationTarget } from '../../core/notification-target.js';
import { pushedNotification } from '../../core/push.js';
import { Spinner } from '../../components/ui.js';
import { HubPage } from '../../components/templates/templates.js';
import { Banner } from '../../components/ui/Feedback.js';
import { INBOX_PATH, refreshInbox } from './notification-data.js';

/** `#…` encodé → texte, ou rien si l'encodage est invalide. */
function fragment(hash: string): string | null {
  try {
    return hash.length > 1 ? decodeURIComponent(hash.slice(1)) : null;
  } catch {
    return null;
  }
}

export function NotificationOpenRedirect(): ReactNode {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me, switchCompany } = useAuth();
  const [failure, setFailure] = useState<string>();
  // Une seule bascule par avis : le profil change pendant qu'elle se fait, et l'effet repasse.
  const switchingTo = useRef<string | undefined>(undefined);
  useEffect(() => {
    const notification = pushedNotification(fragment(hash));
    const member = notification?.member;
    if (member && me && member !== me.id) {
      if (switchingTo.current === member) return;
      const other = membershipsOf(me).find((m) => m.userId === member && m.status === 'actif');
      // Entreprise quittée depuis l'envoi : rien à ouvrir ici.
      if (!other) {
        navigate(INBOX_PATH, { replace: true });
        return;
      }
      switchingTo.current = member;
      switchCompany(member).catch((error: unknown) => {
        setFailure(error instanceof Error ? error.message : t('commun.erreurInattendue'));
      });
      return;
    }
    if (notification && !notification.read_at) {
      // Le marquage n'attend pas : hors ligne, l'élément s'ouvre quand même.
      void api(`/notifications/${notification.id}/read`, { method: 'POST' })
        .then(() => refreshInbox(queryClient), () => undefined);
    }
    navigate((notification && notificationTarget(notification)) ?? INBOX_PATH, { replace: true });
  }, [hash, me, navigate, queryClient, switchCompany]);
  if (failure) {
    return (
      <HubPage header={{ title: t('compteEntreprises.titre') }}>
        <Banner tone="danger" action={{ label: t('notifications.titre'), onClick: () => navigate(INBOX_PATH, { replace: true }) }}>
          {failure}
        </Banner>
      </HubPage>
    );
  }
  return <Spinner variant="page" />;
}
